import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { isCompetitionAverageModeAtom } from '@/atoms/queryParams';
import { teamsLeagueAverageNormalisedAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { hasEmptyInFilters } from '@/utils/api';
import { unwrapWithLoading } from '@/utils/jotai';
import { objSnakeToCamel } from '@/utils/queries';
import { fetchClientAtom } from '../client';
import { radarDataAtom } from '../radar';
import { teamRadarTableColumnsAtom } from '../tableColumns';
import { teamRadarTemplatesAtom } from '../userConfigs';
import {
  comparisonTeamRadarDataAtom,
  unwrappedComparisonTeamMinutesPlayed360Atom,
  unwrappedComparisonTeamMinutesPlayedAtom,
} from './teamComparison';
import { teamMetricsAtom } from './teamRadar';

export const teamLeagueAverageRadarFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: teamLeagueAverageRadarFilterAtom } = teamLeagueAverageRadarFilterDebounceObject;

const rawTeamLeagueAverageRadarDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const teamLeagueAverageFilterParams = get(teamLeagueAverageRadarFilterAtom);

  const queryKey = ['teamLeagueAverageRadarData', teamLeagueAverageFilterParams] as const;

  const queryFn = async ({ queryKey: [, filterParams] }: { queryKey: typeof queryKey }) => {
    if (hasEmptyInFilters(filterParams)) return {} satisfies Record<string, number>;

    // API returns a flat { [snake_case_metric_key]: number } record
    return (await fetch(teamsLeagueAverageNormalisedAggregatesUrl(filterParams))) as Record<string, number>;
  };

  return { queryKey, queryFn };
});

export const teamLeagueAverageRadarDataAtom = unwrapWithLoading(
  radarDataAtom(rawTeamLeagueAverageRadarDataAtom, teamMetricsAtom, teamRadarTemplatesAtom, teamRadarTableColumnsAtom),
  [],
);

export const teamLeagueAverageIsLoadingAtom = atom(get => {
  if (!get(isCompetitionAverageModeAtom)) return false;
  const { isLoading: radarIsLoading } = get(teamLeagueAverageRadarDataAtom);
  return radarIsLoading;
});

export const teamRenderedComparisonSourceAtom = atom(get =>
  get(isCompetitionAverageModeAtom) ? get(teamLeagueAverageRadarDataAtom) : get(comparisonTeamRadarDataAtom),
);

const teamLeagueAverageAggregatesDataAtom = atom(async get => {
  // KeysToCamelCase<Record<string, number>> is structurally Record<string, number> for generic string keys
  const teamLeagueAverageRadarData = objSnakeToCamel((await get(rawTeamLeagueAverageRadarDataAtom)).data) as Record<
    string,
    number
  >;

  return {
    minutesPlayed: teamLeagueAverageRadarData.minutesPlayed ?? 0,
    minutesPlayed360: teamLeagueAverageRadarData.minutesPlayed360 ?? 0,
  };
});

const teamLeagueAverageMinutesPlayedAtom = atom(
  async get => (await get(teamLeagueAverageAggregatesDataAtom)).minutesPlayed,
);
export const unwrappedTeamLeagueAverageMinutesPlayedAtom = unwrap(
  teamLeagueAverageMinutesPlayedAtom,
  prev => prev ?? 0,
);

const teamLeagueAverageMinutesPlayed360Atom = atom(
  async get => (await get(teamLeagueAverageAggregatesDataAtom)).minutesPlayed360,
);

export const unwrappedTeamLeagueAverageMinutesPlayed360Atom = unwrap(
  teamLeagueAverageMinutesPlayed360Atom,
  prev => prev ?? 0,
);

export const unwrappedTeamRenderedComparisonMinutesPlayedAtom = atom(get =>
  get(isCompetitionAverageModeAtom)
    ? get(unwrappedTeamLeagueAverageMinutesPlayedAtom)
    : get(unwrappedComparisonTeamMinutesPlayedAtom),
);

export const unwrappedTeamRenderedComparisonMinutesPlayed360Atom = atom(get =>
  get(isCompetitionAverageModeAtom)
    ? get(unwrappedTeamLeagueAverageMinutesPlayed360Atom)
    : get(unwrappedComparisonTeamMinutesPlayed360Atom),
);
